import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { CurrencyInput } from 'ui-components/components/currency-input';
import { FlexColumn, FlexRow, Gap } from 'ui-components/components/flex';
import { FloatTextArea as UntypedFloatTextArea } from 'ui-components/components/float-text-area';
import { Text, TextType } from 'ui-components/components/text';

import { messages as detailsMessages } from './purchase-order-details-messages';
import { messages } from './purchase-order-payment-details-messages';
import styles from './purchase-order-details.styles.module.less';
import { SimpleDropdown } from '../../../../../components/simple-dropdown';
import Global from '../../../../../store/global-selectors';
import { purchaseOrderProperties } from '../../data/purchase-order-properties';
import { usePurchaseOrderContext } from '../../purchase-order-context';
import { PurchaseOrderSectionId } from '../../purchase-order-expandable-section-provider';
import { ExpandableSection } from '../expandable-section';

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
const FloatTextArea = UntypedFloatTextArea as any;

const noteToSupplierMaxLength = 2000;
const noteToSupplierPlaceholder = `0/${noteToSupplierMaxLength}`;

export const PurchaseOrderPaymentDetails = () => {
  const { purchaseOrder, setPurchaseOrder, paymentTerms, paymentStatusOptions, currencies, validateForm, setFieldTouched } =
    usePurchaseOrderContext() as any; // oxlint-disable-line @typescript-eslint/no-explicit-any
  const defaultCurrencyCode = useSelector(Global.selectSellerDefaultCurrency);
  const currencyCode: string = purchaseOrder.currencyCode || defaultCurrencyCode;

  const handleInputChange = (field: string, value: unknown) => {
    setPurchaseOrder((order: Record<string, unknown>) => {
      const updatedOrder = {
        ...order,
        [field]: value,
      };

      setFieldTouched(field);
      validateForm(updatedOrder);
      return updatedOrder;
    });
  };

  const headerContent = <Text textType={TextType.H4} value={messages.paymentDetails} />;

  const paymentDetailsContent = (
    <FlexRow className={styles['edit-row']}>
      <FlexColumn className={styles.column} gap={Gap.Space8}>
        <Text className={styles['label-text']} textType={TextType.Label} value={detailsMessages.currency} />
        <SimpleDropdown
          defaultTitle={<FormattedMessage {...detailsMessages.selectCurrency} />}
          dropdownMenuShouldHaveWidthOfButton
          items={currencies}
          onItemSelected={(value) => handleInputChange(purchaseOrderProperties.currency, value)}
          selectedItemKey={purchaseOrder.currencyCode}
        />
        <Text className={styles['label-text']} textType={TextType.Label} value={detailsMessages.paymentTerms} />
        <SimpleDropdown
          defaultTitle={<FormattedMessage {...detailsMessages.selectPaymentTerms} />}
          dropdownMenuShouldHaveWidthOfButton
          items={paymentTerms}
          onItemSelected={(value) => handleInputChange(purchaseOrderProperties.paymentTerms, value)}
          selectedItemKey={purchaseOrder.paymentTerms}
        />
        <Text className={styles['label-text']} textType={TextType.Label} value={detailsMessages.noteToSupplier} />
        <FloatTextArea
          maxLength={noteToSupplierMaxLength}
          onChange={(value: string) => handleInputChange(purchaseOrderProperties.noteToSupplier, value)}
          placeholder={noteToSupplierPlaceholder}
          value={purchaseOrder.noteToSupplier}
        />
      </FlexColumn>
      <FlexColumn className={styles.column} gap={Gap.Space8}>
        <Text className={styles['label-text']} textType={TextType.Label} value={detailsMessages.paymentStatus} />
        <SimpleDropdown
          defaultTitle={<FormattedMessage {...detailsMessages.selectPaymentStatus} />}
          dropdownMenuShouldHaveWidthOfButton
          items={paymentStatusOptions}
          onItemSelected={(value) => handleInputChange(purchaseOrderProperties.paymentStatus, value)}
          selectedItemKey={purchaseOrder.paymentStatus}
        />
        <Text className={styles['label-text']} textType={TextType.Label} value={messages.shippingCost} />
        <CurrencyInput
          className={styles['currency-field']}
          currencyCode={currencyCode}
          disallowNegative
          onChange={(value: string | number | null | undefined) =>
            handleInputChange(purchaseOrderProperties.shippingCost, parseFloat(String(value)) || 0)
          }
          value={purchaseOrder.shippingCost || 0}
        />
        <Text className={styles['label-text']} textType={TextType.Label} value={messages.taxes} />
        <CurrencyInput
          className={styles['currency-field']}
          currencyCode={currencyCode}
          disallowNegative
          onChange={(value: string | number | null | undefined) =>
            handleInputChange(purchaseOrderProperties.taxes, parseFloat(String(value)) || 0)
          }
          value={purchaseOrder.taxes || 0}
        />
        <Text className={styles['label-text']} textType={TextType.Label} value={messages.tariffs} />
        <CurrencyInput
          className={styles['currency-field']}
          currencyCode={currencyCode}
          disallowNegative
          onChange={(value: string | number | null | undefined) =>
            handleInputChange(purchaseOrderProperties.tariffs, parseFloat(String(value)) || 0)
          }
          value={purchaseOrder.tariffs || 0}
        />
      </FlexColumn>
    </FlexRow>
  );

  return (
    // @ts-expect-error ExpandableSection is untyped JS with optional props inferred as required
    <ExpandableSection
      ariaLabelMessageDescriptor={messages.paymentDetailsSection}
      className={styles['shipping-details-wrapper']}
      contentClassName={styles['expandable-content-container']}
      headerClassName={styles['expandable-header-container']}
      headerContent={headerContent}
      sectionContent={paymentDetailsContent}
      sectionId={PurchaseOrderSectionId.PaymentDetails}
    />
  );
};
